import {
  type ContactInfoCardProps,
  type DropdownOption,
  type InquiryCoverageCardProps,
  type InquiryDetailHeaderProps,
  type InquiryInfoCardProps,
  type NameWithAvatarCardProps,
  type NoteMaudCardV2Props,
  type ProviderAvatarBlockCardProps,
  Avatar,
  ChakraBox,
  ChakraHStack,
  ChakraVStack,
  DateFormat,
  Name,
  ProviderAvatarBlock,
  Text,
} from '@equip.health/ui';
import type { AvatarUserType } from '@equip.health/ui/lib/types/src/components/Avatar';
import type { ContactMethod } from '@equip.health/ui/lib/types/src/components/Contact/Contact';
import { capitalize, find } from 'lodash';

import type {
  DischargePlanTaskType,
  TaskCardProps,
} from '~/components/common/Tasks';
import {
  type AttachmentType,
  NoteStatus,
} from '~/lib/constants/AddPatientNoteModal.constants';
import { VITE_MAUD_BASE_URL } from '~/lib/constants/env';
import { SurveyName } from '~/lib/constants/survey/SurveyName';
import { SurveyResponseStatus } from '~/lib/constants/survey/SurveyResponseStatus';
import { toLocalMidnight } from '~/lib/util/date.util';
import { buildUniversalSelectSearchTerm } from '~/lib/util/inquiry.util';
import { getPreferredFirstName } from '~/lib/util/patient.util';
import { Path } from '~/routes/constants';

import type { ProvidersSearchData } from './useProvidersSearchData';
import { DateTime } from 'luxon';
import PatientNoteAttachment from '../../patients/profile/tabs/patientNotesTab/PatientNoteAttachment';
import type { PatientCoverage } from '~/lib/constants/patients.constants';
import { InquiryStatusValue } from '~/lib/constants/inquiry.constants';
import {
  getDisplayNameOrLegalNameForProvider,
  getProviderStateAtActionTime,
} from '~/lib/util/user.util';
import { getLatestOutOfOfficeEndDate } from '~/lib/util/profile.util';
import ProviderAvatarTooltipWrapper from '~/components/common/ProviderAvatarBlockTooltipWrapper';
import {
  type AppointmentSectionData,
  getAppointmentCardDisplayProps,
} from '~/lib/util/appointment.util';

export type FromInquiry = {
  infoProps: InquiryInfoCardProps;
  nextContactDate?: Date;
};
export const fromInquiry = (
  inquiry: InquiryDetailResponse,
  patient: Patient,
): FromInquiry => {
  const infoProps: InquiryInfoCardProps = {
    channel: inquiry?.channel?.name,
    channelType: inquiry?.channelType?.name,
    closedReason: inquiry?.closedReason?.name,
    created: inquiry ? new Date(inquiry.inquiryDate) : null,
    heardAboutEquip: inquiry?.equipSource?.name,
    inquirerRole: inquiry?.inquirerRole?.name,
    inquiryNote: inquiry?.inquiryNotes || null,
    modeOfInquiry: inquiry?.informationSource?.name,
    targetTreatmentProgram:
      inquiry?.targetTreatmentProgram?.description || null,
    tellUsMore: inquiry?.tellUsMore || null,
    timezone: patient?.timezone ?? '',
  };

  // Next contact date is returned as UTC midnight. We must convert to local midnight for display.
  const nextContactDate = inquiry?.nextContactDate
    ? toLocalMidnight(new Date(inquiry.nextContactDate))
    : null;
  return { infoProps, nextContactDate };
};

export type InquiryDetailHeaderData = Pick<
  InquiryDetailHeaderProps,
  | 'birthDate'
  | 'channel'
  | 'chosenName'
  | 'closedDate'
  | 'closedReason'
  | 'createdDate'
  | 'diagnosis'
  | 'language'
  | 'firstName'
  | 'lastName'
  | 'pronouns'
  | 'state'
  | 'status'
  | 'targetTreatmentProgram'
  | 'timezone'
  | 'uniqueUrl'
>;

export type GetInquiryHeaderProps = {
  inquiry: InquiryDetailResponse;
  patient: Patient;
  primaryDiagnosis?: string;
  timezoneCode?: string;
};

export const getInquiryHeaderProps = ({
  inquiry,
  patient,
  primaryDiagnosis,
  timezoneCode,
}: GetInquiryHeaderProps): InquiryDetailHeaderData => {
  const status = inquiry?.status?.name;
  const isClosed = status === InquiryStatusValue.CLOSED;
  return {
    birthDate:
      !isClosed && patient?.dateOfBirth ? new Date(patient.dateOfBirth) : null,
    channel:
      !isClosed && !!inquiry?.channel?.name ? inquiry.channel.name : null,
    chosenName: patient?.chosenName,
    closedDate:
      isClosed && inquiry?.closedAt ? new Date(inquiry.closedAt) : null,
    closedReason:
      isClosed && !!inquiry?.closedReason?.name
        ? inquiry.closedReason.name
        : null,
    createdDate:
      !isClosed && !!inquiry?.inquiryDate
        ? new Date(inquiry.inquiryDate)
        : null,
    diagnosis: !isClosed && !!primaryDiagnosis ? primaryDiagnosis : null,
    firstName: patient?.firstName,
    language:
      !isClosed && !!patient?.languages?.[0] ? patient?.languages?.[0] : null,
    lastName: patient?.lastName,
    pronouns: patient?.pronounsOnRecord
      ? patient.pronounsOnRecord.join(', ')
      : null,
    state: !isClosed && !!patient?.state ? patient.state : null,
    status,
    targetTreatmentProgram:
      inquiry?.targetTreatmentProgram?.description || null,
    timezone: !isClosed && !!timezoneCode ? timezoneCode : null,
    uniqueUrl: `${VITE_MAUD_BASE_URL}${Path.MAUD_INQUIRIES}?id=${inquiry?.id}`,
  };
};

export const getAdmissionTeamPropsOnProviderData = (
  inquiry: InquiryDetailResponse,
  providers?: Record<string, GetProviderByIdResponse>,
): ProviderAvatarBlockCardProps[] => {
  return (inquiry?.assignments ?? []).reduce(
    (assignments: ProviderAvatarBlockCardProps[], assignment: Assignment) => {
      const provider = providers?.[assignment.userId];
      if (!provider) return assignments;
      const oooEndDate = getLatestOutOfOfficeEndDate(provider?.outOfOffice);
      const providerHistoryData = getProviderStateAtActionTime(
        provider?.providerHistory,
        assignment.createdAt,
      );
      const deactivatedDate = DateTime.fromISO(
        provider?.deactivatedDate,
      ).startOf('day');
      const deactivatedDateFormatted = deactivatedDate?.isValid
        ? deactivatedDate.toFormat(DateFormat.fullMonthDayYear)
        : null;
      return [
        ...assignments,
        {
          assignmentId: assignment.id,
          avatarUrl: provider?.avatar?.thumbnailUrl || provider?.avatar?.url,
          chosenName: '',
          credentials:
            provider?.credentialsDetails.map((credential) => credential.code) ||
            [],
          deactivatedDate: deactivatedDateFormatted,
          displayName: {
            firstName: provider?.displayFirstName || '',
            lastName: provider?.displayLastName || '',
          },
          firstName: provider?.displayFirstName || provider?.firstName,
          id: assignment.userId,
          lastName: provider?.displayLastName || provider?.lastName,
          legalName: {
            firstName: provider?.firstName || '',
            lastName: provider?.lastName || '',
          },
          oooDate: oooEndDate,
          otherPronoun: provider?.otherPronounText,
          pronouns:
            provider?.pronounsDetails.map((pronoun) => pronoun.name) || [],
          providerTypeName:
            providerHistoryData?.providerTypeDetails?.name ||
            assignment.providerTypeName,
          size: 'medium' as const,
        },
      ];
    },
    [],
  );
};

export type FromNotes = {
  count: number;
  noteProps: (NoteMaudCardV2Props & { createdDateTime?: string | null })[];
  previousContactDate?: Date;
};

/**
 * Extract note card props and previous contact date from notes response.
 * @param notes list of PatientNoteResponseType
 * @param users map of users
 */
export interface FromNotesType extends NoteMaudCardV2Props {
  createdDateTime?: string | null;
  noteTemplateRevisionId?: string | null;
  noteTemplateRevisionData?: Record<string, unknown> | null;
  noteTemplateRevisionSurveyJsId?: string | null;
}

export const fromNotes = (
  notes: GetPatientInquiryNotesResponse,
  providers?: Record<string, GetProviderByIdResponse>,
  patientId?: string,
): FromNotes => {
  const noteProps: FromNotesType[] = [];
  let previousContactDate: Date = null;

  if (notes?.data && providers) {
    notes?.data.forEach((n) => {
      const provider = providers?.[n.createdBy];

      if (!previousContactDate && n.noteStatus === NoteStatus.SIGNED) {
        previousContactDate = new Date(n.activityDate);
      }

      const attachmentsProp = n.attachments
        ? n.attachments.map((a: AttachmentType) => (
            <PatientNoteAttachment
              attachment={a}
              id={`${n.noteExternalId}__attachment-id__${a.attachmentExternalId}`}
              key={`${n.noteExternalId}__attachment-id__${a.attachmentExternalId}`}
              patientId={patientId}
            />
          ))
        : null;

      const assigneeName =
        provider &&
        getDisplayNameOrLegalNameForProvider(
          provider?.displayFirstName,
          provider?.firstName,
          provider?.displayLastName,
          provider?.lastName,
        );

      noteProps.push({
        activityMedium: capitalize(n.noteCommunicationMethod),
        activityWith: (n.activityWith || [])
          .map((x) => capitalize(x.activityWithName))
          .sort((a, b) => a.localeCompare(b))
          .join(', '),
        assignee: {
          id: n.createdBy,
          name: assigneeName,
          ...(provider && {
            src:
              provider?.avatar?.thumbnailUrl || provider?.avatar?.url || null,
          }),
          userType: 'provider',
        },
        attachments: attachmentsProp,
        createdDateTime: n.createdDateTime,
        date: n.activityDate,
        id: n.noteExternalId,
        isDraft: n.noteStatus === NoteStatus.DRAFT,
        noteContent: n.noteContent,
        noteTemplateRevisionData: n.noteTemplateRevisionData,
        noteTemplateRevisionId: n.noteTemplateRevisionId,
        noteTemplateRevisionSurveyJsId: n.noteTemplateRevisionSurveyJsId,
        noteType: n.noteType, // TODO add
        title: n.title, // TODO add
      });
    });
  }
  return { count: notes?.count, noteProps, previousContactDate };
};

/**
 * Extract coverage section props from patient coverage responses.
 * @param inquiry Inquiry response containing in network status.
 * @param coverages list of PatientCoverage
 */
export const getCoverageSectionProps = (
  inquiry: InquiryDetailResponse,
  coverages: PatientCoverage[],
): InquiryCoverageCardProps[] => {
  return coverages.map((c) => {
    const inquiryCoverage = inquiry?.coverages?.find(
      (inqCov) => inqCov.id === c.coverageExternalId,
    );
    const isInNetwork = inquiryCoverage
      ? inquiryCoverage.isInNetwork
      : c.isInNetwork;

    return {
      coverage: {
        alternativeId: c.alternativeId,
        coveragePriority: c.coveragePriority
          ? c.coveragePriority.toString()
          : null,
        customPayorName: c.customPayorName,
        endDate: c.endDate,
        groupId: c.groupId,
        isActive: c.isActive,
        isInNetwork,
        patientRelationToSubscriber: c.patientRelationToSubscriber,
        payor: c.payor,
        providerServicesPhoneNumber: c.providerServicesPhoneNumber,
        startDate: c.startDate,
        stateCode: c.stateCode,
        subscriberDateOfBirth: c.subscriberDateOfBirth
          ? DateTime.fromISO(c.subscriberDateOfBirth).toFormat('LLL dd, yyyy')
          : null,
        subscriberFirstName: c.subscriberFirstName,
        subscriberId: c.subscriberId,
        subscriberLastName: c.subscriberLastName,
      },
      id: c.coverageExternalId,
    };
  });
};

/**
 * Extract appointment section props from appointment response
 * @param appointmentResponse GetAppointmentDetailsResponse
 */
export const getAppointmentSectionProps = (
  appointmentResponse: GetAppointmentDetailsResponse,
): AppointmentSectionData => {
  const appointments: AppointmentSectionData['appointments'] = [];
  let numUpcoming: number = 0;
  const now = new Date();

  if (appointmentResponse?.data) {
    appointmentResponse.data.forEach((a) => {
      appointments.push(
        getAppointmentCardDisplayProps({
          appointmentExternalId: a.appointmentExternalId,
          appointmentStartDateTime: a.appointmentStartDateTime,
          host: a.host,
          title: a.subject ?? a.appointmentTypeLabel,
        }),
      );
      if (new Date(a.appointmentStartDateTime) > now) {
        numUpcoming++;
      }
    });
  }
  return { appointments, numUpcoming };
};

export const getPatientSectionProps = (
  patients: Patient[],
): NameWithAvatarCardProps[] => {
  return patients.map((patient) => {
    return {
      avatarSrc: patient.avatarSrc,
      firstName: getPreferredFirstName(
        patient.chosenName,
        patient.firstName,
        false,
      ),
      id: patient.userId,
      lastName: patient.lastName,
      userType: 'patient',
    };
  });
};

export const getContactSectionProps = (
  contacts?: PatientContact[],
): ContactInfoCardProps[] => {
  return (contacts ?? []).map((c) => {
    const methods: { method: ContactMethod; value: string }[] = [
      { method: 'mobilePhone', value: c.cellPhone },
      { method: 'homePhone', value: c.homePhone },
      { method: 'email', value: c.email },
      { method: 'fax', value: c.faxNumber },
      {
        method: 'officePhone',
        value:
          c.officePhone && c.officeExtension
            ? `${c.officePhone} ext.${c.officeExtension}`
            : c.officePhone,
      },
    ];

    return {
      address: c.address,
      contact: {
        chosenName: c.chosenName,
        firstName: c.firstName,
        isOngoingProvider: !!c.isOngoingProvider,
        isPrimary: !!c.isPrimaryContact,
        isReferrer: !!c.isReferringProvider,
        isSupport: !!c.isSupport,
        lastName: c.lastName,
        methods: methods.filter((x) => x.value),
        prefix: c.prefix,
        pronouns: c?.pronouns ? c?.pronouns?.join(', ') : null,
        relationToPatient: c.relationToPatient,
      },
      id: c.contactExternalId,
    };
  });
};

export const getContactSectionPropFromPatient = (
  patient: Patient,
  contacts?: Partial<PatientContact>[],
): ContactInfoCardProps | null => {
  const methods: { method: ContactMethod; value?: string }[] = [
    { method: 'mobilePhone', value: patient.phoneNumber },
    { method: 'homePhone', value: patient.homePhone },
    { method: 'email', value: patient.email },
    { method: 'fax', value: patient.faxNumber },
    {
      method: 'officePhone',
      value:
        patient.officeNumber && patient.officeExtension
          ? `${patient.officeNumber} ext.${patient.officeExtension}`
          : patient.officeNumber,
    },
  ];
  const methodsWithValues = methods.filter((x) => x.value) as {
    method: ContactMethod;
    value: string;
  }[];

  // Return null if there are no contact methods
  if (methodsWithValues.length === 0) {
    return null;
  }

  // Patient is primary contact if no other contact is primary
  const patientIsPrimary = !find(
    contacts,
    (contact) => contact.isPrimaryContact,
  );

  const patientIsSupport = patient?.hasAuthId && patient?.isAccountActive;

  return {
    contact: {
      chosenName: patient.chosenName,
      firstName: patient.firstName,
      isPrimary: patientIsPrimary,
      isReferrer: false,
      isSupport: !!patientIsSupport,
      lastName: patient.lastName,
      methods: methodsWithValues,
      prefix: null,
      pronouns: patient?.pronounsOnRecord
        ? patient?.pronounsOnRecord?.join(', ')
        : null,
      relationToPatient: 'Patient',
    },
    id: patient.userId,
  };
};

export const getTaskProps = (
  taskSummary?: TaskSummaryResponse,
): TaskCardProps[] => {
  return (taskSummary?.data ?? []).map((taskSummary) => ({
    assignees: (taskSummary.userAssignees ?? []).map((assignee) => ({
      id: assignee.externalId,
      name: getDisplayNameOrLegalNameForProvider(
        assignee?.displayFirstName,
        assignee.firstName,
        assignee?.displayLastName,
        assignee.lastName,
      ),
      size: 'xs',
      userType: assignee.userType as AvatarUserType,
      src: assignee?.avatar?.thumbnailUrl || assignee?.avatar?.url || null,
    })),
    category: taskSummary.category,
    createdDate: taskSummary.createdDate,
    dueDate: taskSummary.dueDate
      ? toLocalMidnight(new Date(taskSummary.dueDate))
      : null,
    id: taskSummary.taskExternalId,
    status: taskSummary.statusCode,
    subCategory: taskSummary.subCategory,
    title: taskSummary.name,
    dischargePlanTaskType: taskSummary?.type as DischargePlanTaskType,
  }));
};

export const mapNoteCommunicationMethodsToDropdownOptions = (
  noteCommunicationMethods: string[],
): DropdownOption[] => {
  return noteCommunicationMethods?.map((method) => ({
    label: method,
    value: method,
  }));
};

export const getIsPatientPrimaryContact = (
  patientId?: string,
  primaryContactId?: string,
) => {
  if (!primaryContactId) {
    return true;
  }

  return patientId === primaryContactId;
};

const buildSearchData = (provider: GetProviderByIdResponse) =>
  buildUniversalSelectSearchTerm(
    provider.firstName + provider.lastName + provider.providerTypeDetails?.name,
  );

export const getMenuOptions = (id: string, searchData: ProvidersSearchData) =>
  searchData.dataList.map(
    (p: GetProviderByIdResponse): ProviderUniversalSelectOption => {
      return {
        ...p,
        label: (
          <ChakraHStack gap="10px">
            <Avatar
              name={p.firstName + ' ' + p.lastName}
              size="xs"
              userType="provider"
            />
            <ChakraVStack alignItems="start" spacing="2px">
              <Name
                firstName={p.firstName}
                id={p.userId}
                lastName={p.lastName}
              />
              {p.providerTypeDetails && (
                <Text
                  color="neutral.secondary"
                  id={`${id}__${p.userId}`}
                  variant="caption"
                >
                  {p.providerTypeDetails.name}
                </Text>
              )}
            </ChakraVStack>
          </ChakraHStack>
        ),
        searchData: buildSearchData(p),
        value: p.userId,
      };
    },
  );

export const getUniversalSelectOptionsForProviders = (
  id: string,
  searchData: GetProviderByIdResponse[],
  filteredOutProviderIds?: string[],
) =>
  searchData
    ?.filter((p) => !filteredOutProviderIds?.includes(p.userId))
    ?.map((p: GetProviderByIdResponse): ProviderUniversalSelectOption => {
      const legalName = {
        firstName: p.firstName,
        lastName: p.lastName,
      };
      const displayName = {
        firstName: p.displayFirstName,
        lastName: p.displayLastName,
      };

      const isDisplayName = displayName.firstName && displayName.lastName;
      const endDate = getLatestOutOfOfficeEndDate(p.outOfOffice);

      return {
        ...p,
        label: (
          <ChakraBox padding="6px 0px">
            <ProviderAvatarBlock
              avatarUrl={p?.avatar?.thumbnailUrl || p?.avatar?.url}
              displayName={isDisplayName ? displayName : undefined}
              id={`${id}__AvatarBlock__${p.userId}`}
              legalName={legalName}
              oooDate={endDate}
              providerTypeName={p.providerTypeDetails?.name}
              shouldShowOOOIcon={!!endDate}
              shouldShowProviderType
              variant="standard"
            />
          </ChakraBox>
        ),
        primaryText: `${p.firstName} ${p.lastName}`,
        value: p.userId,
      };
    });

export const SurveyResponseStatusSortOrder: Record<
  SurveyResponseStatus,
  number
> = {
  [SurveyResponseStatus.IN_PROGRESS]: 1,
  [SurveyResponseStatus.REQUESTED]: 2,
  [SurveyResponseStatus.NEW]: 3,
  [SurveyResponseStatus.DONE]: 4,
  [SurveyResponseStatus.INCOMPLETE]: 5,
  [SurveyResponseStatus.VOID]: 6,
};

export const sortAssessmentsByStatusAndCreatedDate = (
  assessments: AssessmentSummary[],
): AssessmentSummary[] => {
  return [...assessments].sort((a, b) => {
    const orderByStatusValue =
      SurveyResponseStatusSortOrder[a.status.name] -
      SurveyResponseStatusSortOrder[b.status.name];
    if (orderByStatusValue === 0) {
      return (
        new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
      );
    }
    return orderByStatusValue;
  });
};

export const getIntakeAssessment = (
  assessments: AssessmentSummary[],
): AssessmentSummary => {
  const nonVoidedIntakeAssessments = (assessments ?? []).filter(
    (a) =>
      a.survey?.name === SurveyName.ADMISSIONS_INTAKE &&
      a.status.name !== SurveyResponseStatus.VOID,
  );
  if (nonVoidedIntakeAssessments.length > 1) {
    return sortAssessmentsByStatusAndCreatedDate(assessments)[0];
  }
  return nonVoidedIntakeAssessments[0];
};

export const getTooltipWrapper = (assigneeId?: string, actionTime?: string) => {
  const tooltipWrapper = ({ children }) => (
    <ProviderAvatarTooltipWrapper
      actionTime={actionTime}
      isRoleAtAction
      placement="top"
      providerExternalId={assigneeId}
    >
      {children}
    </ProviderAvatarTooltipWrapper>
  );

  return tooltipWrapper;
};
